import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Table, ApplicationUIContext } from '@yuhuinc/ui-library';
import { Form, Button, Input, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { YuhuModules, Published } from '../../../payments/Fees/components/Form/constants';

import { getTranslatedString } from 'helpers/i18n';

import { searchFeeTemplates } from '../../store/actions';

const COLUMNS = [
  {
    title: getTranslatedString('landlordPortal.fees.internalName'),
    dataIndex: 'internal_name',
    key: 'internal_name',
  },
  {
    title: getTranslatedString('landlordPortal.fees.internalDescription'),
    dataIndex: 'internal_description',
    key: 'internal_description',
  },
];

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px ${({ isMobile }) => (isMobile ? '16px' : '24px')};
`;

const SelectFeesTable = ({ selectedUnitId, editableFeeTemplate, onAdd, closeDrawer }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const additionalInformationForm = Form.useFormInstance();
  const applicationSelectedFeeTemplates = additionalInformationForm.getFieldValue('fee_templates');
  const [selectedFeeTemplateRow, setSelectedFeeTemplateRow] = useState(editableFeeTemplate || null);
  const [isLoading, setIsLoading] = useState(false);
  const [feeTemplatesData, setFeeTemplatesData] = useState([]);

  const loadFeeTemplates = ({ query } = {}) => {
    const defaultParams = {
      unit_ids: [selectedUnitId],
      yuhu_module: YuhuModules.applications,
      status: Published,
      query,
    };

    if (selectedUnitId) {
      setIsLoading(true);
      searchFeeTemplates(defaultParams)
        .done((response) => {
          const responseTemplates = response.fee_templates;
          const selectableFeeTemplates = responseTemplates.filter(
            (responseTemplate) =>
              !applicationSelectedFeeTemplates.some(
                (selectedFeeTemplate) => selectedFeeTemplate.id === responseTemplate.id
              )
          );

          setFeeTemplatesData(selectableFeeTemplates);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('Request failed', error);
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    loadFeeTemplates();
  }, [searchFeeTemplates]);

  const handleChange = (event) => {
    const { value: nextValue } = event.target;

    loadFeeTemplates({ query: nextValue });
  };

  const handleAddFeeTemplate = () => {
    onAdd({
      id: selectedFeeTemplateRow.id,
      internal_name: selectedFeeTemplateRow.internal_name,
      fee_events: selectedFeeTemplateRow.fee_events,
    });
    closeDrawer();
  };

  const rowSelection = {
    selectedRowKeys: selectedFeeTemplateRow && [selectedFeeTemplateRow.id],
    onSelect: (record) => setSelectedFeeTemplateRow(record),
    type: 'radio',
    fixed: true,
  };

  return (
    <StyledContainer isMobile={isMobile}>
      <Form.Item name="fee_query">
        <Input onChange={handleChange} suffix={<SearchOutlined />} />
      </Form.Item>

      <Table
        hideActionAndSearch
        dataSource={feeTemplatesData}
        columns={COLUMNS}
        size={isMobile ? 'middle' : 'small'}
        loading={isLoading}
        rowKey={(record) => record.id}
        rowSelection={rowSelection}
        pagination={{ pageSize: 10, showSizeChanger: false }}
        scroll={{ x: isMobile }}
      />

      <Space>
        <Button type="primary" disabled={!selectedFeeTemplateRow} onClick={handleAddFeeTemplate}>
          {getTranslatedString('landlordPortal.applicationManagement.inviteLeadToApply.add')}
        </Button>

        <Button htmlType="button" onClick={closeDrawer}>
          {getTranslatedString('landlordPortal.applicationManagement.inviteLeadToApply.cancel')}
        </Button>
      </Space>
    </StyledContainer>
  );
};

SelectFeesTable.propTypes = {
  selectedUnitId: PropTypes.number,
  editableFeeTemplate: PropTypes.object,
  onAdd: PropTypes.func.isRequired,
  closeDrawer: PropTypes.func.isRequired,
};

SelectFeesTable.defaultProps = {
  selectedUnitId: null,
  editableFeeTemplate: null,
};

export default SelectFeesTable;
